// Normalizador de tipos de ítem del vault: categorías canónicas fijas (las
// mismas que usaba el sidebar del Vault.js viejo, l.223-236), icono IconKit,
// etiqueta y los campos que se muestran/editan por categoría.
//
// Un ítem guarda su tipo "en bruto" en `data.type` (login, oauth, card...).
// Los tipos importados de 1Password/Bitwarden (ImportExport.js) usan además
// `apiCredential`, `secureNote` y `softwareLicense`: no son categorías nuevas,
// son alias de `api` y `note` para que cuenten y se agrupen igual.

import { ReactComponent as GridIcon } from '../assets/icons/grid.svg';
import { ReactComponent as StarIcon } from '../assets/icons/star.svg';
import { ReactComponent as LockIcon } from '../assets/icons/lock.svg';
import { ReactComponent as UsersIcon } from '../assets/icons/users.svg';
import { ReactComponent as CreditCardIcon } from '../assets/icons/credit-card.svg';
import { ReactComponent as UserIcon } from '../assets/icons/user.svg';
import { ReactComponent as RepeatIcon } from '../assets/icons/repeat.svg';
import { ReactComponent as FileTextIcon } from '../assets/icons/file-text.svg';
import { ReactComponent as KeyRoundIcon } from '../assets/icons/key-round.svg';
import { ReactComponent as ShieldIcon } from '../assets/icons/shield.svg';
import { ReactComponent as GlobeIcon } from '../assets/icons/globe.svg';
import { ReactComponent as DatabaseIcon } from '../assets/icons/database.svg';
import { ReactComponent as FileIcon } from '../assets/icons/file.svg';
import { ReactComponent as FingerprintIcon } from '../assets/icons/fingerprint.svg';

// Orden y contenido: igual que sidebar.* en components/Vault.js l.223-236.
export const CATEGORIES = [
  { id: 'all', labelKey: 'dashboard.categories.all', icon: GridIcon },
  { id: 'favorites', labelKey: 'dashboard.categories.favorites', icon: StarIcon },
  { id: 'login', labelKey: 'dashboard.categories.login', icon: LockIcon },
  { id: 'oauth', labelKey: 'dashboard.categories.oauth', icon: UsersIcon },
  { id: 'card', labelKey: 'dashboard.categories.card', icon: CreditCardIcon },
  { id: 'identity', labelKey: 'dashboard.categories.identity', icon: UserIcon },
  { id: 'subscription', labelKey: 'dashboard.categories.subscription', icon: RepeatIcon },
  { id: 'note', labelKey: 'dashboard.categories.note', icon: FileTextIcon },
  { id: 'password', labelKey: 'dashboard.categories.password', icon: KeyRoundIcon },
  { id: 'ssh', labelKey: 'dashboard.categories.ssh', icon: ShieldIcon },
  { id: 'api', labelKey: 'dashboard.categories.api', icon: GlobeIcon },
  { id: 'database', labelKey: 'dashboard.categories.database', icon: DatabaseIcon },
  { id: 'file', labelKey: 'dashboard.categories.file', icon: FileIcon },
  { id: 'passkey', labelKey: 'dashboard.categories.passkey', icon: FingerprintIcon },
];

export const CATEGORY_ICON = CATEGORIES.reduce((acc, c) => { acc[c.id] = c.icon; return acc; }, {});

// raw `data.type` -> categoría canónica. Lo que no aparece aquí (u
// otp_vault, que se oculta aparte) cae en "note".
const RAW_TYPE_TO_CATEGORY = {
  login: 'login',
  oauth: 'oauth',
  card: 'card',
  identity: 'identity',
  subscription: 'subscription',
  password: 'password',
  ssh: 'ssh',
  api: 'api',
  apiCredential: 'api',
  database: 'database',
  file: 'file',
  note: 'note',
  secureNote: 'note',
  softwareLicense: 'note',
  passkey: 'passkey',
};

export function categoryOf(rawType) {
  return RAW_TYPE_TO_CATEGORY[rawType] || 'note';
}

// Sentinela del vault OTP: nunca es un ítem real, ni cuenta ni se lista.
export function isHiddenSystemItem(item) {
  const name = item?.name || item?.title || '';
  return name === '__otp_entries__' || item?.type === 'otp_vault' || item?.category === 'otp_vault';
}

// Campos por categoría, en el orden en que se muestran/editan. `type` es el
// tipo de input; `secret` marca los que se enseñan detrás de un ojo/copiar.
export const FIELD_DEFS = {
  login: [
    { key: 'username', labelKey: 'dataEntry.username', type: 'text' },
    { key: 'password', labelKey: 'dataEntry.password', type: 'password', secret: true, generate: true },
    { key: 'url', labelKey: 'dataEntry.url', type: 'url' },
    { key: 'notes', labelKey: 'dataEntry.notes', type: 'textarea' },
  ],
  oauth: [
    { key: 'oauthProvider', labelKey: 'oauth.providerAccount', type: 'text' },
    { key: 'oauthEmail', labelKey: 'dataEntry.email', type: 'text' },
    { key: 'oauthUsername', labelKey: 'dataEntry.username', type: 'text' },
    { key: 'url', labelKey: 'dataEntry.url', type: 'url' },
    { key: 'notes', labelKey: 'dataEntry.notes', type: 'textarea' },
  ],
  card: [
    { key: 'cardHolder', labelKey: 'dataEntry.cardHolder', type: 'text' },
    { key: 'cardNumber', labelKey: 'dataEntry.cardNumber', type: 'password', secret: true },
    { key: 'cardType', labelKey: 'dataEntry.cardType', type: 'text' },
    { key: 'expiryDate', labelKey: 'dataEntry.expiryDate', type: 'text' },
    { key: 'cvv', labelKey: 'dataEntry.cvv', type: 'password', secret: true },
    { key: 'cardPin', labelKey: 'dataEntry.cardPin', type: 'password', secret: true },
    { key: 'issuingBank', labelKey: 'dataEntry.issuingBank', type: 'text' },
    { key: 'billingAddress', labelKey: 'dataEntry.billingAddress', type: 'textarea' },
    { key: 'notes', labelKey: 'dataEntry.notes', type: 'textarea' },
  ],
  identity: [
    { key: 'firstName', labelKey: 'dataEntry.firstName', type: 'text' },
    { key: 'lastName', labelKey: 'dataEntry.lastName', type: 'text' },
    { key: 'birthDate', labelKey: 'dataEntry.birthDate', type: 'text' },
    { key: 'email', labelKey: 'dataEntry.email', type: 'text' },
    { key: 'phone', labelKey: 'dataEntry.phone', type: 'text' },
    { key: 'passportNumber', labelKey: 'dataEntry.passportNumber', type: 'text', secret: true },
    { key: 'socialSecurity', labelKey: 'dataEntry.socialSecurity', type: 'text', secret: true },
    { key: 'licenseNumber', labelKey: 'dataEntry.licenseNumber', type: 'text' },
    { key: 'company', labelKey: 'dataEntry.company', type: 'text' },
    { key: 'jobTitle', labelKey: 'dataEntry.jobTitle', type: 'text' },
    { key: 'department', labelKey: 'dataEntry.department', type: 'text' },
    { key: 'occupation', labelKey: 'dataEntry.occupation', type: 'text' },
    { key: 'gender', labelKey: 'dataEntry.gender', type: 'text' },
    { key: 'address', labelKey: 'dataEntry.address', type: 'textarea' },
    { key: 'website', labelKey: 'dataEntry.url', type: 'url' },
    { key: 'notes', labelKey: 'dataEntry.notes', type: 'textarea' },
  ],
  subscription: [
    { key: 'provider', labelKey: 'dataEntry.provider', type: 'text' },
    { key: 'plan', labelKey: 'dataEntry.plan', type: 'text' },
    { key: 'cost', labelKey: 'dataEntry.cost', type: 'text' },
    { key: 'currency', labelKey: 'dataEntry.currency', type: 'text' },
    { key: 'billingCycle', labelKey: 'dataEntry.billingCycle', type: 'text' },
    { key: 'startDate', labelKey: 'dataEntry.startDate', type: 'text' },
    { key: 'renewalDate', labelKey: 'dataEntry.renewalDate', type: 'text' },
    { key: 'autoRenew', labelKey: 'dataEntry.autoRenew', type: 'checkbox' },
    { key: 'username', labelKey: 'dataEntry.username', type: 'text' },
    { key: 'password', labelKey: 'dataEntry.password', type: 'password', secret: true },
    { key: 'url', labelKey: 'dataEntry.url', type: 'url' },
    { key: 'notes', labelKey: 'dataEntry.notes', type: 'textarea' },
  ],
  password: [
    { key: 'password', labelKey: 'dataEntry.password', type: 'password', secret: true, generate: true },
    { key: 'notes', labelKey: 'dataEntry.notes', type: 'textarea' },
  ],
  ssh: [
    { key: 'sshKey', labelKey: 'dataEntry.sshKey', type: 'textarea', secret: true },
    { key: 'notes', labelKey: 'dataEntry.notes', type: 'textarea' },
  ],
  api: [
    { key: 'apiKey', labelKey: 'dataEntry.apiKey', type: 'password', secret: true },
    { key: 'apiSecret', labelKey: 'dataEntry.apiSecret', type: 'password', secret: true },
    { key: 'url', labelKey: 'dataEntry.endpointUrl', type: 'url' },
    { key: 'notes', labelKey: 'dataEntry.notes', type: 'textarea' },
  ],
  database: [
    { key: 'dbHost', labelKey: 'dataEntry.host', type: 'text' },
    { key: 'dbPort', labelKey: 'dataEntry.port', type: 'text' },
    { key: 'dbName', labelKey: 'dataEntry.database', type: 'text' },
    { key: 'username', labelKey: 'dataEntry.username', type: 'text' },
    { key: 'password', labelKey: 'dataEntry.password', type: 'password', secret: true, generate: true },
    { key: 'notes', labelKey: 'dataEntry.notes', type: 'textarea' },
  ],
  file: [
    { key: 'fileName', labelKey: 'dataEntry.fileName', type: 'file' },
    { key: 'notes', labelKey: 'dataEntry.notes', type: 'textarea' },
  ],
  note: [
    { key: 'notes', labelKey: 'dataEntry.notes', type: 'textarea' },
  ],
  // Sin entrada: un passkey no se crea ni se edita a mano (nace en la
  // extensión o la app al registrarte en una web, ver design/PASSKEYS.md).
  // Al no listar campos, ItemForm no ofrece ninguno técnico para editar y
  // ItemDetail renderiza su propia vista de solo lectura.
};

// Tipo "en bruto" a guardar en `data.type` al crear un ítem nuevo de esta
// categoría. Las categorías con alias legado (note, api) siempre crean con
// el tipo canónico: los legados solo existen para lo ya importado.
export function rawTypeForNewItem(category) {
  return category;
}
